import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { baseListFiltersActions } from '../../_store/actions';
import { baseListFiltersSelectors } from '../../_store/selectors';
import { ListFilterParams } from '../../_store/actions/base-list-filters.actions';
import { isEmptyParams } from './base-list-filter-memory.util';

/**
 * Session-scoped memory of the last active filter selection per list view,
 * keyed by the list's URL path. Backed by the `listFilters` store slice, which
 * is persisted to sessionStorage and wiped on logout.
 *
 * The shared filter pipeline uses it automatically: filters are saved on every
 * URL redirect and restored when a list re-hydrates its form on entry — so no
 * per-route configuration is needed.
 */
@Injectable({ providedIn: 'root' })
export class BaseListFilterMemoryService {
  constructor(private store: Store) {}

  /** Read the stored filter snapshot for a list path (synchronous). */
  getSnapshot(routeKey: string): ListFilterParams | undefined {
    let snapshot: ListFilterParams | undefined;
    this.store
      .select(baseListFiltersSelectors.selectListFilterSnapshot(routeKey))
      .pipe(take(1))
      .subscribe((value) => (snapshot = value));
    return snapshot;
  }

  /** Persist the filter snapshot for a list path; an empty selection clears it. */
  save(routeKey: string, params: ListFilterParams): void {
    if (isEmptyParams(params)) {
      this.store.dispatch(baseListFiltersActions.clearSnapshot({ routeKey }));
      return;
    }
    this.store.dispatch(baseListFiltersActions.saveSnapshot({ routeKey, params }));
  }

  /** Forget the stored filters for a list path. */
  clear(routeKey: string): void {
    this.store.dispatch(baseListFiltersActions.clearSnapshot({ routeKey }));
  }
}
